import React from "react";
import { Box, Menu, MenuItem } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { ChevronDown } from "lucide-react";
import { Button } from "./Button";
import { useTheme as useCustomTheme } from "../theme/useTheme";

/**
 * Reusable table tabs (same look as the AveronCRMERP TableToolbar tabs):
 * no outer box, the active tab is a soft neutral chip with dark text.
 * Expects `options` with `value` and `label` (string or node) and a `selectedValue`.
 */
export const FilterSwitchGroup = ({
  options = [],
  selectedValue,
  onChange,
  disabled = false,
  size = "sm",
  containerSx = {},
  optionsSx = {},
  buttonSx = {},
  rightContent = null,
  dropdownTab = null,
}) => {
  const { neutrals } = useCustomTheme();
  const [dropdownAnchorEl, setDropdownAnchorEl] = React.useState(null);
  // Tab activa en gris neutro (texto oscuro) para no saturar de color de marca.
  const activeBg = alpha(neutrals.gray[900], 0.08);

  const baseContainerSx = React.useMemo(
    () => ({
      display: "inline-flex",
      alignItems: "center",
      flexWrap: "wrap",
      gap: 0.75,
      ...containerSx,
    }),
    [containerSx]
  );

  const baseOptionsSx = React.useMemo(
    () => ({
      display: "inline-flex",
      flexWrap: "wrap",
      alignItems: "center",
      gap: 0.75,
      ...optionsSx,
    }),
    [optionsSx]
  );

  const getButtonSx = (isSelected) => ({
    borderRadius: "6px",
    px: 1.5,
    py: 0.75,
    minWidth: 0,
    height: "32px",
    minHeight: "32px",
    fontSize: "0.75rem",
    fontWeight: 500,
    lineHeight: "20px",
    whiteSpace: "nowrap",
    border: "none",
    transition: "background-color 0.15s, color 0.15s",
    backgroundColor: isSelected ? activeBg : "transparent",
    color: disabled
      ? neutrals.gray[400]
      : isSelected
        ? neutrals.gray[900]
        : neutrals.gray[500],
    "&:hover": {
      backgroundColor: disabled
        ? isSelected
          ? activeBg
          : "transparent"
        : isSelected
          ? activeBg
          : neutrals.gray[50],
      color: disabled
        ? neutrals.gray[400]
        : neutrals.gray[900],
    },
    "&.Mui-disabled": {
      color: neutrals.gray[400],
    },
    opacity: disabled ? 0.75 : 1,
    ...buttonSx,
  });

  const dropdownButtonSx = {
    borderRadius: "6px",
    px: 1.5,
    height: "32px",
    minHeight: "32px",
    fontSize: "0.75rem",
    fontWeight: 500,
    backgroundColor: neutrals.white,
    color: neutrals.gray[700],
    border: `1px solid ${neutrals.gray[200]}`,
    "&:hover": { backgroundColor: neutrals.gray[50] },
    ...buttonSx,
  };

  const dropdownSelectedOption = React.useMemo(() => {
    if (!dropdownTab?.options?.length) return null;
    return (
      dropdownTab.options.find((option) => option.value === dropdownTab.value) ||
      dropdownTab.options[0]
    );
  }, [dropdownTab]);

  const renderIcon = (icon, size = 14) => {
    if (!icon) return null;
    if (React.isValidElement(icon)) return icon;
    const IconComponent = icon;
    return <IconComponent size={size} />;
  };

  return (
    <Box sx={baseContainerSx}>
      {dropdownTab ? (
        <>
          <Button
            variant="outline"
            size={size}
            onClick={(event) => setDropdownAnchorEl(event.currentTarget)}
            aria-label={dropdownTab.ariaLabel || "Seleccionar vista"}
            sx={dropdownButtonSx}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
              {renderIcon(dropdownSelectedOption?.icon)}
              {dropdownTab.showSelectedLabel !== false
                ? dropdownSelectedOption?.label
                : null}
              {dropdownTab.showChevron !== false ? (
                <ChevronDown size={14} />
              ) : null}
            </Box>
          </Button>

          <Menu
            anchorEl={dropdownAnchorEl}
            open={Boolean(dropdownAnchorEl)}
            onClose={() => setDropdownAnchorEl(null)}
          >
            {dropdownTab.options.map((option) => (
              <MenuItem
                key={option.value}
                selected={option.value === dropdownTab.value}
                onClick={() => {
                  dropdownTab.onChange?.(option.value);
                  setDropdownAnchorEl(null);
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  {renderIcon(option.icon, 16)}
                  {option.label}
                </Box>
              </MenuItem>
            ))}
          </Menu>
        </>
      ) : null}

      <Box sx={baseOptionsSx}>
        {options.map(({ value, label }) => {
          const isSelected = selectedValue === value;
          return (
            <Button
              key={value}
              aria-pressed={isSelected}
              variant="ghost"
              size={size}
              disabled={disabled}
              onClick={() => {
                if (disabled) return;
                onChange?.(value);
              }}
              sx={getButtonSx(isSelected)}
            >
              {label}
            </Button>
          );
        })}
      </Box>

      {rightContent ? (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
          }}
        >
          {rightContent}
        </Box>
      ) : null}
    </Box>
  );
};

export default FilterSwitchGroup;
